import React, { useEffect, useState } from 'react';
import { Scale } from 'lucide-react';
import './App.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    // Simulate initial loading progress
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 80) {
          clearInterval(progressInterval);
          return 80; // Stop at 80% until image loads
        }
        return prev + Math.random() * 15;
      });
    }, 100);

    // Preload hero background image
    const heroImg = new Image();
    heroImg.onload = () => {
      // Complete the loading progress
      setLoadingProgress(100);
      // Wait a bit for the progress animation, then hide loading screen
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    };
    heroImg.src = '/assets/images/2.jpg';
    
    // Preload services background image
    const servicesImg = new Image();
    servicesImg.src = '/assets/images/4.jpg';

    return () => {
      clearInterval(progressInterval);
    };
  }, []);

  const openCalendly = () => {
    // Open Calendly in a new tab with default styles
    window.open('https://calendly.com/parisottomarco2', '_blank');
  };

  const heroStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('/assets/images/2.jpg')`
  };

  const servicesStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('/assets/images/4.jpg')`
  };

  return (
    <div className="App">
      {/* Loading Screen */}
      {isLoading && (
        <div className="loading-screen">
          <div className="loading-content">
            <div className="loading-icon">
              <Scale size={60} strokeWidth={1.5} />
            </div>
            <h2 className="loading-title">Marco Parisotto</h2>
            <p className="loading-subtitle">Attorney At Law</p>
            <div className="loading-bar">
              <div 
                className="loading-progress" 
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`main-content ${isLoading ? 'hidden' : 'visible'}`}>
        {/* Hero Section */}
        <section className="hero" style={heroStyle}>
          <div className="hero-content">
            <div className="logo">
              <div className="logo-text">
                <span className="logo-main">Marco Parisotto</span>
                <span className="logo-sub">Attorney At Law</span>
              </div>
            </div>
            
            <h1>Contract Attorney For<br />California Family Law Firms</h1>
            <h2>Flexible, Court-Ready Support — Delivered Remotely</h2>
            
            <button className="cta-button hero-button" onClick={openCalendly}>SCHEDULE A FREE CONSULTATION</button>
          </div>
          <div className="hero-image">
            <div className="gavel-rings"></div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="intro">
          <div className="container">
            <h2>Legal Support You Can Rely On — Without the Overhead</h2>
            <p>
              I help California family law firms manage caseloads with efficient, high-quality legal support — including motion drafting, discovery, client declarations, judgment packages, legal research, remote court appearances, independent case management and co-counsel representation. Services are tailored to your firm's workflow, allowing you to offload time-consuming tasks while maintaining the standards your clients expect.
            </p>
          </div>
        </section>

        {/* Services Section */}
        <section className="services" style={servicesStyle}>
          <div className="container">
            <div className="services-content">
              <div className="services-text">
                <h2>Flat-Fee Services</h2>
                
                <div className="pricing-table">
                  <div className="pricing-header-row">
                    <div className="header-col">Service</div>
                    <div className="header-col">Flat Fee</div>
                    <div className="header-col">Service</div>
                    <div className="header-col">Flat Fee</div>
                  </div>
                  
                  <div className="pricing-row">
                    <div className="service-name">
                      <strong>Request for Order Package</strong>
                      <span className="service-description">(FL-300, up to 10-page declaration, attachments)</span>
                    </div>
                    <div className="service-price">$550</div>
                    <div className="service-name">
                      <strong>Preliminary Financial Disclosures</strong>
                      <span className="service-description">Includes FL-140, FL-141, FL-142, FL-150, and supporting documentation binder</span>
                    </div>
                    <div className="service-price">$325–$400</div>
                  </div>
                  
                  <div className="pricing-row">
                    <div className="service-name">
                      <strong>Judgment Package with Agreement</strong>
                      <span className="service-description">(MSA, FL-180, FL-170, FL-190, and required Judicial Council forms)</span>
                    </div>
                    <div className="service-price">$750-$850</div>
                    <div className="service-name">
                      <strong>Trial Brief or Complex Motion Drafting</strong>
                      <span className="service-description">(Includes legal research, statement of facts, issues, argument, supporting declarations and formatting for filing; tailored to case complexity)</span>
                    </div>
                    <div className="service-price">$800–$900</div>
                  </div>
                  
                  <div className="pricing-row">
                    <div className="service-name">
                      <strong>Discovery Requests</strong>
                      <span className="service-description">(20 Requests for Production, 10 Requests for Admission, 10 Special Interrogatories)</span>
                    </div>
                    <div className="service-price">$450</div>
                    <div className="service-name">
                      <strong>Discovery Responses</strong>
                      <span className="service-description">(Objections, verified responses, and verifications)</span>
                    </div>
                    <div className="service-price">$500</div>
                  </div>
                  
                  <div className="pricing-row">
                    <div className="service-name">
                      <strong>Remote Court Appearance</strong>
                      <span className="service-description">(≤1 hour)</span>
                    </div>
                    <div className="service-price">$250</div>
                    <div className="service-name">
                      <strong>Remote Deposition Representation</strong>
                      <span className="service-description">(≤2 hours)</span>
                    </div>
                    <div className="service-price">$350</div>
                  </div>
                  
                  <div className="pricing-row pricing-hourly pricing-full-width">
                    <div className="service-name">
                      <strong>Attorney Hourly Rate</strong>
                      <span className="service-description">(billed hour)</span>
                    </div>
                    <div className="service-price">$100/hr</div>
                  </div>
                </div>
                
                <div className="services-highlight">
                  <h3>Why Flat Fees?</h3>
                  <p>
                    Flat-fee packages offer price certainty for repeatable, document-heavy tasks. All work is delivered court-ready. Flat-fee services include one round of reasonable revisions. Additional work due to new information or changes in scope may be billed hourly, with your prior approval.
                  </p>
                  <p>
                    The services listed above are just a sample of what I offer. I also handle Petition for Dissolution packages, DVRO requests and responses, Updating Declarations, and virtually any family law drafting or procedural task that can be performed remotely.
                  </p>
                  <p>
                    Hourly billing is also available upon request and is well-suited for flexible, open-ended matters or ongoing overflow support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="team">
          <div className="container">
            <div className="team-content">
              <div className="team-text">
                <h2>About Marco Parisotto, Esq.</h2>
                <p>
                  As a California-licensed family law attorney, I provide specialized legal support exclusively to solo and small family law firms across the state. Prior to licensure, I spent two years working remotely as a paralegal in a boutique, high-volume California family law firm, gaining hands-on experience with the demands of fast-paced casework. I currently serve as a contract attorney for a small California family law firm, providing ongoing support across a wide range of matters with minimal supervision. I understand what kind of support growing family law firms need to manage demanding caseloads — and I know how to deliver it with precision, efficiency, and practical expertise.
                </p>
                
                <button className="cta-button secondary" onClick={openCalendly}>SCHEDULE A FREE CONSULTATION</button>
              </div>
              <div className="team-image">
                <img src="/assets/images/1.jpeg" alt="Marco Parisotto, Esq." className="team-photo" />
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="final-cta" id="consultation-section">
          <div className="container">
            <h2>Get a Free 30 Minute Consultation</h2>
            <button className="cta-button final-cta-button" onClick={openCalendly}>SCHEDULE A FREE CONSULTATION</button>
            
            <p className="quick-question">Have a quick question? Feel free to call or email anytime.</p>
            
            <p>
              Free up your time and strengthen your practice — starting with a free consultation.
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <p>California Bar Number: SBN 363019</p>
            <div className="contact-info">
              <p>Phone: <a href="tel:(561)-827-6249">(561)-827-6249</a> | Email: <a href="mailto:legal@marcoesq.com">legal@marcoesq.com</a></p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;